<script>
  import { onMount, onDestroy } from 'svelte';
  import { fly } from 'svelte/transition';
  import * as moment from 'moment';
  import Cookies from 'js-cookie';
  import css from '../scss/index.scss';

  import {
    errorState,
    eventState,
    streamState,
    accessState,
    playerState,
    drmState,
    themeState,
    chatState,
    roomsState,
    broadcasterState,
  } from '../js/state.js';
  import {
    retrieveStreamInfo,
    postError,
    clearError,
    retrieveTheme,
    requestPlayVideoIfNotPaused,
    retrieveBroadcasterSettings,
    setReceiver,
    joinRoom,
    streamLicenseChallenge,
  } from '../js/actions.js';
  import {
    EVENT_STATE_INIT,
    EVENT_STATE_PRE,
    EVENT_STATE_LIVE,
    EVENT_STATE_POST,
    EVENT_STATE_ERROR,
    EVENT_STATE_NOT_FOUND,
    PLAYER_STATE_PLAYING,
    STREAM_STATUS_ACTIVE,
    STREAM_STATUS_IDLE,
    STREAM_STATUS_DRAFT,
    STREAM_STATUS_UNAVAILABLE_EARLY,
    STREAM_STATUS_UNAVAILABLE_EXPIRED,
    STREAM_TYPE_EXTERNAL,
    STREAM_TYPE_RTMP,
    STREAM_TYPE_VOD,
    COOKIE_VIEWER_TOKEN,
    CHAT_COLLAPSE_SIZE,
    CHAT_GENERAL_CHANNEL_NAME,
  } from '../js/constants.js';
  import {
    PAGE_NOT_FOUND,
    STREAM_STARTS_MOMENTARILY,
    STREAM_HAS_ENDED,
    THANK_YOU_FOR_YOUR_PATIENCE,
    VOD_HAS_EXPIRED,
    SHOW_HOVER_TEXT,
  } from '../js/l10n/en_US.js';

  import { parseURLComponents } from '../js/libs/url.js';

  import Rollbar from './Rollbar.svelte';
  import TabArea from './TabArea.svelte';
  import ErrorBar from './ErrorBar.svelte';
  import GoogleAnalytics from './GoogleAnalytics.svelte';
  import StreamPlayer from './StreamPlayer.svelte';
  import ExternalPlayer from './ExternalPlayer.svelte';
  import EventDetails from './EventDetails.svelte';
  import AccessCodeForm from './AccessCodeForm.svelte';
  import Countdown from './Countdown.svelte';
  import DelayNotice from './DelayNotice.svelte';
  import Footer from './Footer.svelte';

  let receiver;

  let overlay = false,
    pageNotFound = false,
    source,
    id,
    room,
    code,
    chatEnabled = false,
    tabsVisible = true,
    roomsEnabled = false,
    licenseInterval = null,
    licenseIntervalPeriod = 30000,
    isLive = false;

  function addStyleSheet(url) {
    const head = document.getElementsByTagName('head')[0];
    var style = document.createElement('link');
    style.href = url;
    style.type = 'text/css';
    style.rel = 'stylesheet';
    head.append(style);
  }

  function onResize(w, h) {
    return;
  }

  function toggleTabsVisible() {
    tabsVisible = !tabsVisible;
  }

  // We need to check back in every so often just in case times change on the
  // backend. This should also guarantee we enter the event when it starts.
  let timer,
    getEventInfoIntervalPre = 15000,
    getEventInfoIntervalPost = 15000,
    getEventInfoRandomInterval = 5000,
    postStartTimeframe = 60000 * 3;

  function getEventInfo() {
    // Get the data
    retrieveStreamInfo(source, id);

    // Preventative, in case this gets called more than once
    try {
      clearTimeout(timer);
    } catch (e) {}

    // Calculate next call
    let delay = getEventInfoIntervalPre; // timeout delay for next call
    let e = $eventState;
    if (e && e.start) {
      const diff = moment(e.start).diff(moment());

      // Has event already started, with some cushion?
      if (diff < -1 * postStartTimeframe) {
        delay = getEventInfoIntervalPost;
      }
    }

    delay = delay + getEventInfoRandomInterval * Math.random();
    timer = setTimeout(getEventInfo, delay);
  }

  function postLicense() {
    if (!$streamState || !$drmState.token || !navigator.onLine) return;
    streamLicenseChallenge(
      $drmState.token,
      $drmState.challenge,
      $playerState.position,
      $playerState.status
    );
    // todo consider moving persitent storage to somewhere else, we're not
    // following best practice here (i.e. we're storing a jwt token accessible by
    // javascript). In the current scenario this jwt is very limited in what can be
    // done with it. If we ever implement ecommerce we may want to consider beefing up.
    const path = document.location.pathname.split('/').slice(0, 4).join('/');
    if ($drmState.valid) {
      Cookies.set(COOKIE_VIEWER_TOKEN, $drmState.token, {
        sameSite: 'strict',
        path: path,
        secure: true,
      });
    } else {
      Cookies.remove(COOKIE_VIEWER_TOKEN, {
        sameSite: 'strict',
        path: path,
        secure: true,
      });
    }
  }

  window.onerror = (message, source, lineno, colno, error) => {
    console.warn('[App] Uncaught error:', message, source, lineno, colno, error);
  };

  onMount(() => {
    try {
      const url = parseURLComponents(document.location.pathname);
      (source = url.source), (id = url.id), (code = url.code), (room = url.room);
      timer = setTimeout(getEventInfo, 100);
      retrieveTheme(window.location.host, source, id);
      retrieveBroadcasterSettings(source, id);
    } catch (e) {
      console.log('Problem retrieving initial event, theme or broadcaster settings: ' + e);
      pageNotFound = true;
    }
  });

  // Reactive
  $: if (
    source &&
    id &&
    $accessState.token &&
    $broadcasterState.settings &&
    $broadcasterState.settings.app_key &&
    ($streamState.use_chat || $streamState.use_reactions) &&
    !receiver
  ) {
    let app_key = $broadcasterState.settings.app_key;
    let cluster = $broadcasterState.settings.cluster;
    let token = $accessState.token;
    // Dynamically import chat lib if chat enabled
    // and instantiate instance of receiver
    import('../js/receivers/pusher.js').then((obj) => {
      try {
        let pr = obj.PusherReceiver;
        receiver = new pr(source, id, token, {
          key: app_key,
          cluster: cluster,
        });
      } catch (e) {
        console.log(e.toString());
        return;
      }
      receiver.connect();
      setReceiver(receiver);
    });
  }

  $: if (source && id && room && roomsEnabled && $accessState.token) {
    joinRoom(source, id, room, $accessState.token);
  }

  // todo clear the cookie if the access code in the URL differs from the access code in the cookie
  $: if ($streamState && $streamState.drm && $drmState && !$drmState.valid) {
    Cookies.remove(COOKIE_VIEWER_TOKEN);
  }

  $: chatEnabled = $streamState && ($streamState.use_chat || $streamState.use_reactions);
  $: roomsEnabled = $streamState && $streamState.use_rooms;
  $: embeddedPagesEnabled = $streamState && $streamState.show_iframe_embed;

  $: (() => {
    overlay = !!$accessState.success;
    !!$accessState.success &&
      $eventState &&
      $eventState.state === EVENT_STATE_LIVE &&
      setTimeout(requestPlayVideoIfNotPaused, 500);
  })();

  $: if (themeState && !$themeState.loaded) {
    addStyleSheet($themeState.stylesheet);
  }

  $: if (source && id && $drmState && $drmState.token && !licenseInterval) {
    postLicense();
    licenseInterval = true; // prevents a bunch of requests from being fired at first
    setTimeout(function () {
      licenseInterval = setInterval(postLicense, licenseIntervalPeriod);
    }, licenseIntervalPeriod * Math.random());
  }

  $: if (
    pageNotFound ||
    (themeState && $themeState.loaded && $eventState && $eventState.state !== EVENT_STATE_INIT)
  ) {
    setTimeout(() => {
      const interim = document.getElementById('interim-loading');
      interim && interim.remove();
    }, 1000);
  }

  $: {
    // Determine the 'Live' status of a stream
    if ($streamState.type === STREAM_TYPE_VOD) {
      isLive = false;
    } else if ($eventState.state === EVENT_STATE_LIVE) {
      isLive = true;
    } else if ($eventState.state === EVENT_STATE_POST) {
      if ($streamState.state === STREAM_STATUS_ACTIVE) {
        isLive = true;
      } else if (!$playerState.ended && $playerState.position !== 0) {
        isLive = true;
      } else {
        isLive = false;
      }
    } else {
      isLive = false;
    }
  }
</script>

<div>
  <GoogleAnalytics />
  <Rollbar />
  <div
    class="dimmer {$playerState && $playerState.state === PLAYER_STATE_PLAYING
      ? 'lights-out'
      : 'lights-on'}"
  />
  {#if !$eventState}
    <div class="container-fluid main-content">
      <div class="row">
        <div class="container components-container">
          <div class="event-details" style="text-align: center;">...</div>
        </div>
      </div>
    </div>
  {:else if $eventState.state === EVENT_STATE_ERROR}
    <div class="container-fluid main-content">
      <div class="row">
        <div class="container components-container">
          <div class="event-details" style="text-align: center;">{THANK_YOU_FOR_YOUR_PATIENCE}</div>
        </div>
      </div>
    </div>
  {:else if pageNotFound || $eventState.state === EVENT_STATE_NOT_FOUND}
    <div class="container-fluid main-content">
      <div class="row">
        <div class="container components-container">
          <div class="event-details page-not-found" style="text-align: center;">
            {PAGE_NOT_FOUND}
          </div>
        </div>
      </div>
    </div>
  {:else}
    <div class="default-content">
      {#if $errorState.error}
        <ErrorBar error={$errorState.error} type={$errorState.type} onClear={clearError} />
      {/if}

      {#if !overlay}
        <div id="overlay">
          <AccessCodeForm {source} {id} {code} />
        </div>
      {/if}

      <div
        class="container-fluid main-content {chatEnabled && tabsVisible && $accessState.token
          ? 'main-content-chat'
          : 'main-content-no-chat'}"
        class:blur={!overlay}
      >
        <div class="row main-content-row">
          <div
            id="video-and-details"
            class={chatEnabled && tabsVisible && $accessState.token
              ? 'video-main-col'
              : 'video-no-chat'}
          >
            <!-- Start of if statements by streamState.type -->
            <!-- Video on Demand -->
            {#if $streamState.type === STREAM_TYPE_VOD}
              {#if $streamState.state === STREAM_STATUS_UNAVAILABLE_EARLY}
                <Countdown date={$streamState.vod.viewing_start} />
              {:else if $streamState.state === STREAM_STATUS_UNAVAILABLE_EXPIRED}
                <div class="player-banner player-banner-dark">
                  {$eventState.player_msg_post || VOD_HAS_EXPIRED}
                </div>
              {:else}
                <StreamPlayer url={$streamState.url} {onResize} />
              {/if}

              <!-- External Livestreaming -->
            {:else if $streamState.type === STREAM_TYPE_EXTERNAL}
              <!-- Pre stream -->
              {#if $eventState.state === EVENT_STATE_PRE}
                <Countdown date={$eventState.start} />
              {:else if $eventState.state === EVENT_STATE_LIVE || ($eventState.state === EVENT_STATE_POST && $streamState.state === STREAM_STATUS_ACTIVE)}
                <ExternalPlayer
                  url={$streamState.url}
                  sourceType={$streamState.external_source_type}
                />

                <!-- Post stream - note special case with active stream post show -->
              {:else if $eventState.state === EVENT_STATE_POST && $streamState.state !== STREAM_STATUS_ACTIVE}
                <div class="player-banner player-banner-dark">
                  {$eventState.player_msg_post || STREAM_HAS_ENDED}
                </div>
              {/if}

              <!-- Standard Livestreaming -->
            {:else if $streamState.type === STREAM_TYPE_RTMP}
              <!-- Pre stream -->
              {#if $eventState.state === EVENT_STATE_PRE}
                <Countdown date={$eventState.start} />

                <!-- During stream - note special case with active stream post show -->
              {:else if $eventState.state === EVENT_STATE_LIVE || ($eventState.state === EVENT_STATE_POST && ($streamState.state === STREAM_STATUS_ACTIVE || (!$playerState.ended && $playerState.position !== 0)))}
                {#if [STREAM_STATUS_IDLE, STREAM_STATUS_DRAFT].includes($streamState.state) && ($playerState.ended || $playerState.position === 0)}
                  <div class="player-banner player-banner-dark">
                    {$eventState.player_msg_idle || STREAM_STARTS_MOMENTARILY}
                  </div>
                {:else}
                  <StreamPlayer url={$streamState.url} {onResize} live={isLive} />
                {/if}

                <!-- Post stream - note special case with active stream post show -->
              {:else if $eventState.state === EVENT_STATE_POST && $streamState.state !== STREAM_STATUS_ACTIVE}
                <div class="player-banner player-banner-dark">
                  {$eventState.player_msg_post || STREAM_HAS_ENDED}
                </div>
              {/if}
            {/if}
            <!-- End if statements by streamState.type -->

            {#if $roomsState.current && $roomsState.current !== CHAT_GENERAL_CHANNEL_NAME}
              <DelayNotice />
            {/if}

            <div class="row" id="event-details-row">
              <EventDetails
                {onResize}
                tipJarTitle={$eventState.tip_jar_title}
                tipJarButtons={$eventState.tip_jar_buttons}
                title={$eventState.title}
                venue={$eventState.venue}
                live={isLive}
              />
            </div>
          </div>
        </div>
        <div id="foot-wrap">
          <Footer />
        </div>
      </div>

      {#if $accessState.token && $chatState && $eventState && ((embeddedPagesEnabled && $eventState.embedded_pages && $eventState.embedded_pages.length > 0) || chatEnabled)}
        {#if tabsVisible}
          <div
            class="chat-content"
            class:blur={!overlay}
            transition:fly={{ x: 340, duration: 200 }}
          >
            <div class="video-side-col">
              <TabArea
                {source}
                {id}
                {chatEnabled}
                {embeddedPagesEnabled}
                {toggleTabsVisible}
                embeddedPages={$eventState.embedded_pages}
              />
            </div>
          </div>
        {:else if window.innerWidth > CHAT_COLLAPSE_SIZE}
          <div class="chat-visibility-control">
            <i
              class="icon-keyboard_return"
              on:click={() => {
                toggleTabsVisible();
              }}
            >
              <span class="hover-text">{SHOW_HOVER_TEXT}</span>
            </i>
          </div>
        {/if}
      {/if}
    </div>
  {/if}
</div>
